<template>
  <div class="container-fluid full-height-page-wrapper">
    <div class="row">

      <FinancesClientsModal
        v-model="createModal.show"
        :projects="getProjects"
        :countries="getCountries"
        :isLoading="isCreateModalLoading"
        @hide="hideCreateModal"
        @submit="createFinanceClient"
      />
      <FinancesClientsModal
        v-model="editModal.show"
        :title="editModal.title"
        :projects="getProjects"
        :countries="getCountries"
        :data="editModal.data"
        :isLoading="isEditModalLoading"
        @hide="hideEditModal"
        @submit="editFinanceClient"
      />

      <div class="col-12">
        <div class="page-table-header">
          <PageHeader showBreadcrumbs page-title="Clients">
            <template #buttons>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowFilter ? 'secondary' : 'info'"
                @click="toggleShowFilter"
              >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Filters
              </b-button>
              <b-button
                v-if="$can('create-finances_clients')"
                variant="success"
                class="button"
                @click="showCreateModal"
                :disabled="isDataPageLoading"
              >
                Create
              </b-button>
            </template>
          </PageHeader>

          <Filters
            v-if="$can(permissions.ReadAllFinancesClients)"
            v-show="isShowFilter"
            textButtonSubmit="Filter"
            :data="getTableFilters"
            :isLoading="isDataPageLoading && isInitialDataLoaded"
            @submit="setFilterClients"
          />

          <div v-if="isDataPageLoading && !isInitialDataLoaded" class="content-loading-wrapper">
            <Preloader class="centered-preloader" />
          </div>

          <TableForPage
            v-if="getClientsTableData.length"
            :isDataLoading="isDataPageLoading && isInitialDataLoaded"
            ref="table"
            :tableData="getClientsTableData"
            :fields="table.fields"
            :perPage="getClients.per_page"
            :currentPage="getClients.current_page"
            :totalRows="getClients.total"
            :isEdit="$can(permissions.UpdateAllFinancesClients)"
            :isRemove="$can(permissions.DeleteFinancesClients)"
            isServerRender
            noLocalSorting
            @remove="removeClient"
            @edit="showEditModal"
            @update="updateClients"
          />
          <EmptyStateNoResults
            v-else-if="isInitialDataLoaded && !getClientsTableData.length && !getClients.isAnyData"
            customIcon="folder_off"
            customIconClasses="material-symbols-outlined empty-state__icon"
            title="No clients added"
            description="You haven't added any clients yet. Create one to start managing your client relationships."
            buttonText="CREATE"
            @button-click="handleCreateClick"
          />
          <EmptyStateNoResults
            v-else-if="isInitialDataLoaded && !getClientsTableData.length && getClients.isAnyData"
            @reset-filters="handleResetFilters"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>

import { permissions } from '@mocks/permissions'
import FinanceClientsPartners from '../mixins/FinanceClientsPartners'
import TableForPage from '@tables/TableForPage'
import Filters from '@components/Filter/Filter'
import FinancesClientsModal from '@components/modals/FinancesClientsModal'
import PageHeader from '@components/PageHeader'
import { mapGetters } from 'vuex'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults'

export default {
  name: 'FinancesClients',
  mixins: [FinanceClientsPartners],
  components: {
    TableForPage,
    Filters,
    FinancesClientsModal,
    PageHeader,
    Preloader,
    EmptyStateNoResults
  },
  data () {
    return {
      permissions: permissions,
      isAllOptionsFilterSet: false,
      isClientsDataLoaded: false,
      isCreateModalLoading: false,
      isEditModalLoading: false,
      createModal: {
        show: false,
        title: 'Create client'
      },
      editModal: {
        show: false,
        title: 'Edit client',
        data: null
      },
      table: {
        fields: [
          {
            key: 'first_name',
            label: 'First name',
            sortable: true
          },
          {
            key: 'last_name',
            label: 'Last name',
            sortable: true
          },
          {
            key: 'company_name',
            label: 'Company name'
          },
          {
            key: 'email',
            label: 'Email'
          },
          {
            key: 'phone',
            label: 'Phone'
          },
          {
            key: 'projects',
            label: 'Project',
            formatter: value => {
              return value ? value.map(project => project.name).join(', ') : ''
            }
          },
          {
            key: 'country.name',
            label: 'Country'
          },
          {
            key: 'city',
            label: 'City'
          },
          {
            key: 'address',
            label: 'Address'
          },
          {
            key: 'zip_code',
            label: 'Zip code'
          },
          {
            key: 'actions',
            label: 'Actions'
          }
        ],
        params: {
          filters: null,
          sorting: null,
          currentPage: '?page=1'
        }
      }
    }
  },

  computed: {
    isInitialDataLoaded () {
      return this.isAllOptionsFilterSet && this.isClientsDataLoaded
    },
    ...mapGetters({
      getProjects: 'projects/getProjects',
      getClients: 'finances/getClients'
    }),
    getClientsTableData () {
      return this.getClients.data || []
    },
    getTableFilters () {
      const clientsFilters = {
        ...this.filters,
        'project': {
          type: 'select',
          name: 'project_id',
          label: 'Project',
          selectLabel: 'label',
          valueField: 'id',
          options: this.getProjects,
          value: null
        }
      }

      return clientsFilters || {}
    }
  },
  methods: {
    fetchAllData () {
      this.fetchClients()
      Promise.all([
        this.fetchProjects(),
        this.fetchCountries()
      ])
        .then(() => {
          this.setAllOptionsFilter()
        })
    },
    setAllOptionsFilter () {
      this.getTableFilters.project.options = this.getProjects

      this.isAllOptionsFilterSet = true
    },
    async fetchProjects () {
      return this.$api.projects.getProjects()
    },
    setFilterClients (data) {
      this.setParamsFilters(data)
      this.setParamsCurrentPage()
      this.fetchClients()
    },
    setParamsFilters (data) {
      const filters = Object.values(data).reduce(function (state, current) {
        if (current['value']) {
          const value = typeof current['value'] === 'object'
            ? current['value'][current['valueField']] : current['value']
          state += `&filter[${current.name}]=${value}`
        }
        return state
      }, '')

      this.table.params.filters = filters || null
    },
    fetchClients () {
      let params = this.table.params.currentPage

      if (this.table.params.sorting) {
        params += this.table.params.sorting
      }
      if (this.table.params.filters) {
        params += this.table.params.filters
      }

      return this._toggleDataPageLoading(() => this.$api.finances.getClients(params)
      ).then((result) => {
        this.isClientsDataLoaded = true
        return result
      })
    },
    async removeClient (item) {
      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete the client?'
      )

      if (!confirm) return

      this._toggleDataPageLoading(async () => {
        const deleteResponse = await this.$api.finances.removeClient(item.id)
        this.$snotify.success(
          deleteResponse.data,
          'Success',
          this.$store.state.snotify
        )
        this.updateClients()
      })
    },
    updateClients (data) {
      if (!data) return this.fetchClients()

      const { currentPage, sortDesc, sortBy } = data

      this.setParamsCurrentPage(currentPage)
      this.setParamsSort(sortDesc, sortBy)

      this.fetchClients()
    },
    prepareDataForEditAndCreateRequest (fields) {
      let data = {}

      for (let key in fields) {
        const field = fields[key]
        const name = field['name']
        const value = field['value']
        if (Object.prototype.toString.call(value) === '[object Array]' && value) {
          for (let valueItemKey in value) {
            const valueItemValue = value[valueItemKey]
            data[name] = data[name] ?? []
            data[name][valueItemKey] = (typeof valueItemValue === 'object' && valueItemValue && field['valueField']) ? valueItemValue[field['valueField']] : valueItemValue
          }
        } else {
          data[name] = (typeof value === 'object' && value && field['valueField']) ? value[field['valueField']] : value
        }
      }

      return data
    },
    async createFinanceClient (fields) {
      const data = this.prepareDataForEditAndCreateRequest(fields)

      this.isCreateModalLoading = true
      try {
        await this.$api.finances.createClients(data)
        this.$snotify.success(
          'Client successfully created',
          'Success',
          this.$store.state.snotify
        )
        this.hideCreateModal()
        this.updateClients()
      } catch (error) {
        this.$snotify.error('Error creating client', 'Error')
      } finally {
        this.isCreateModalLoading = false
      }
    },
    async editFinanceClient (fields) {
      const data = this.prepareDataForEditAndCreateRequest(fields)

      this.isEditModalLoading = true
      try {
        await this.$api.finances.editClient(data)
        this.$snotify.success(
          'Client updated successfully',
          'Success',
          this.$store.state.snotify
        )
        this.hideEditModal()
        this.updateClients()
      } catch (error) {
        this.$snotify.error('Error updating client', 'Error')
      } finally {
        this.isEditModalLoading = false
      }
    },
    handleResetFilters () {
      if (this.$refs.filtersComponent) {
        this.$refs.filtersComponent.resetFilter()
      }
    },
    handleCreateClick () {
      this.showCreateModal()
    }
  }
}
</script>

<style scoped>
  .table-wrapper {
    padding-top: 20px;
  }

  .table-wrapper h3 {
    margin-bottom: 30px;
  }

  .entities-table {
    background-color: #fff;
  }

  .entities-table-wrapper {
    max-height: 600px;
    overflow-y: auto;
  }

  .spinner-border {
    border-width: 0.25rem !important;
  }

  .filter-buttons {
    padding-top: 34px;
  }
</style>
